import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, TextField, InputAdornment, IconButton } from "@mui/material";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { AppState } from "redux/store";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ContactTable from "./ContactPageTable";
import { getContactUs } from "redux/actions";

const Contact = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const { contact } = useSelector<AppState, AppState["contactMaster"]>(
    ({ contactMaster }) => contactMaster
  );

  const rowPerPage = 50;
  const [page, setPage] = useState(0);
  const [search, setSearchQuery] = useState<string>("");
  const [searchField, setSearchField] = useState("all");

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Date", value: "date" },
    { label: "Source", value: "source" },
    { label: "Reference", value: "reference" },
    { label: "Name", value: "name" },
    { label: "Mobile", value: "contactNo" },
    { label: "Email", value: "email" },
    { label: "City", value: "city" },
    { label: "Remarks", value: "message" },
  ];

  const exportContactsToCSV = (contactData: any[]) => {
    const csvRows: string[] = [];

    const headers = [
      "Date",
      "Source",
      "Reference",
      "Name",
      "Mobile",
      "Email",
      "City",
      "Remarks",
    ];
    csvRows.push(headers.join(","));

    contactData.forEach((item) => {
      const row = [
        item.createdAt || "",
        item.source || "",
        item.reference || "",
        item.name || "",
        item.contactNo || "",
        item.email || "",
        item.city || "",
        item.message || "",
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "contacts.csv";
    link.click();
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    const offset = page * rowPerPage;
    dispatch(getContactUs(search, offset));
  }, [dispatch, search, page]);

  const onSearchContact = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const filteredContact = search.trim()
    ? contact.filter((item) => {
        const searchTerm = search.trim().toLowerCase();

        if (searchField === "all") {
          return searchFields
            .filter((f) => f.value !== "all")
            .some(({ value }) => {
              const fieldValue = item[value];
              return fieldValue?.toString().toLowerCase().includes(searchTerm);
            });
        } else {
          const fieldValue = item[searchField];
          return fieldValue?.toString().toLowerCase().includes(searchTerm);
        }
      })
    : contact;

  const paginatedContacts = filteredContact.slice(
    page * rowPerPage,
    (page + 1) * rowPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.contactMaster"]}
            </Box>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => exportContactsToCSV(contact)}
            >
              Export
            </Button>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "flex-end",
              width: 1,
            }}
          >
            <TextField
              select
              size="small"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchQuery("");
                setPage(0);
              }}
              SelectProps={{ native: true }}
            >
              {searchFields.map((field) => (
                <option key={field.value} value={field.value}>
                  {field.label}
                </option>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => onSearchContact(e.target.value)}
              placeholder={messages["common.searchHere"] as string}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton
                        onClick={() => onSearchContact("")}
                        size="small"
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <ContactTable contact={paginatedContacts} />
          <AppsPagination
            rowsPerPage={rowPerPage}
            count={filteredContact.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Contact;
